import { AnimatedSprite, Texture } from 'pixi.js';
import { TextureFactory, FSM, Store } from '../../core';
import { IEventEmitter, Point, StatefulAnimation, StateMap, Transitionable, Vector } from '../../interface';
import { BaseObject } from '../';
import PlayerTextureInfo_1 from './Player_1.json';
import PlayerTextureInfo_2 from './Player_2.json';

interface IPlayer extends IEventEmitter {
  hp: number;
  score: number;
  velocity: Vector;
  position: Point;
}

class Player extends BaseObject implements Transitionable {
  public hp: number;
  public score: number;
  private fsm: FSM<IPlayer>;
  private statefulAnimation: StatefulAnimation;
  private animatedSprite: AnimatedSprite;

  constructor() {
    super();
    this.hp = Store.instance.state.hp;
    this.score = Store.instance.state.score;

    const textures_1 = TextureFactory.instance.load(PlayerTextureInfo_1);
    const textures_2 = TextureFactory.instance.load(PlayerTextureInfo_2);

    this.statefulAnimation = {
      IDLE: {
        textures: textures_1.slice(174, 187),
        animationSpeed: 0.3,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      HIT: {
        textures: textures_1.slice(0, 14), // 10행 5번째 ~ 30개
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      HIT2: {
        textures: textures_1.slice(188, 217),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      CLEAR: {
        textures: textures_1.slice(15, 63),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      DIE: {
        textures: textures_1.slice(64, 113),
        animationSpeed: 0.25,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      DIE2: {
        textures: textures_1.slice(114, 173),
        animationSpeed: 0.25,
        offset: { x: 0, y: 0 },
        loop: false,
      },

      MAGIC_0: {
        textures: textures_2.slice(39, 77),
        animationSpeed: 0.8,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      MAGIC_1: {
        textures: textures_2.slice(0, 38),
        animationSpeed: 0.8,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      MAGIC_2: {
        textures: textures_2.slice(78, 116),
        animationSpeed: 0.8,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      MAGIC_3: {
        textures: textures_2.slice(156, 194),
        animationSpeed: 0.8,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      MAGIC_4: {
        textures: textures_2.slice(117, 155),
        animationSpeed: 0.8,
        offset: { x: 0, y: 0 },
        loop: false,
      },
    };

    this.animatedSprite = new AnimatedSprite([Texture.EMPTY]);
    this.animatedSprite.updateAnchor = true;
    this.animatedSprite.onComplete = () => this.fsm.trigger('ANIMATION_DONE');
    this.animatedSprite.scale.set(1.25);
    this.fsm = new FSM<IPlayer>(this, this.states, 'IDLE');
    this.stage.addChild(this.animatedSprite);
  }

  public trigger(...events: Array<string>): void {
    this.fsm.trigger(...events);
  }

  public transition(prevState: string, nextState: string): void {
    const info = this.statefulAnimation[nextState];
    const flipX = this.animatedSprite.scale.x < 0 ? -1 : 1;

    this.animatedSprite.textures = info.textures;
    this.animatedSprite.scale.set(flipX * Math.abs(this.animatedSprite.scale.x), this.animatedSprite.scale.y);
    this.animatedSprite.animationSpeed = info.animationSpeed;
    this.animatedSprite.loop = info.loop;

    this.animatedSprite.gotoAndPlay(0);
  }

  public update(elapsed: number): void {
    super.update(elapsed);
    this.updateZIndex();
    this.fsm.update();
  }

  public hit(damage: number): void {
    this.vibrator.vibrate(24);
    if (this.hp <= 0) return;

    this.hp -= damage;

    if (this.hp <= 0) {
      this.hp = 0;
      this.fsm.trigger('DIE');
      this.emit('DIE');
    } else {
      if (Math.random() < 0.5) {
        this.fsm.trigger('HIT');
      } else {
        this.fsm.trigger('HIT2');
      }
    }
  }

  private get states(): StateMap<IPlayer> {
    return {
      IDLE: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        HIT2: [{ events: ['HIT2'] }],
        MAGIC_0: [{ events: ['MAGIC_0'] }],
        MAGIC_1: [{ events: ['MAGIC_1'] }],
        MAGIC_2: [{ events: ['MAGIC_2'] }],
        MAGIC_3: [{ events: ['MAGIC_3'] }],
        MAGIC_4: [{ events: ['MAGIC_4'] }],
      },
      HIT: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        HIT2: [{ events: ['HIT2'] }],
        MAGIC_0: [{ events: ['MAGIC_0'] }],
        MAGIC_1: [{ events: ['MAGIC_1'] }],
        MAGIC_2: [{ events: ['MAGIC_2'] }],
        MAGIC_3: [{ events: ['MAGIC_3'] }],
        MAGIC_4: [{ events: ['MAGIC_4'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      HIT2: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        HIT2: [{ events: ['HIT2'] }],
        MAGIC_0: [{ events: ['MAGIC_0'] }],
        MAGIC_1: [{ events: ['MAGIC_1'] }],
        MAGIC_2: [{ events: ['MAGIC_2'] }],
        MAGIC_3: [{ events: ['MAGIC_3'] }],
        MAGIC_4: [{ events: ['MAGIC_4'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      DIE: {},
      CLEAR: {},
      MAGIC_0: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        HIT2: [{ events: ['HIT2'] }],
        MAGIC_0: [{ events: ['MAGIC_0'] }],
        MAGIC_1: [{ events: ['MAGIC_1'] }],
        MAGIC_2: [{ events: ['MAGIC_2'] }],
        MAGIC_3: [{ events: ['MAGIC_3'] }],
        MAGIC_4: [{ events: ['MAGIC_4'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      MAGIC_1: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        HIT2: [{ events: ['HIT2'] }],
        MAGIC_0: [{ events: ['MAGIC_0'] }],
        MAGIC_1: [{ events: ['MAGIC_1'] }],
        MAGIC_2: [{ events: ['MAGIC_2'] }],
        MAGIC_3: [{ events: ['MAGIC_3'] }],
        MAGIC_4: [{ events: ['MAGIC_4'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      MAGIC_2: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        HIT2: [{ events: ['HIT2'] }],
        MAGIC_0: [{ events: ['MAGIC_0'] }],
        MAGIC_1: [{ events: ['MAGIC_1'] }],
        MAGIC_2: [{ events: ['MAGIC_2'] }],
        MAGIC_3: [{ events: ['MAGIC_3'] }],
        MAGIC_4: [{ events: ['MAGIC_4'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      MAGIC_3: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        HIT2: [{ events: ['HIT2'] }],
        MAGIC_0: [{ events: ['MAGIC_0'] }],
        MAGIC_1: [{ events: ['MAGIC_1'] }],
        MAGIC_2: [{ events: ['MAGIC_2'] }],
        MAGIC_3: [{ events: ['MAGIC_3'] }],
        MAGIC_4: [{ events: ['MAGIC_4'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      MAGIC_4: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        HIT2: [{ events: ['HIT2'] }],
        MAGIC_0: [{ events: ['MAGIC_0'] }],
        MAGIC_1: [{ events: ['MAGIC_1'] }],
        MAGIC_2: [{ events: ['MAGIC_2'] }],
        MAGIC_3: [{ events: ['MAGIC_3'] }],
        MAGIC_4: [{ events: ['MAGIC_4'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
    };
  }
}

export default Player;
